import React from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import GlobeIcon from '@atlaskit/icon/core/globe';
import Lozenge from '@atlaskit/lozenge';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { webSearchPill_AtlassianConversationAssistantAgent_WebSearchPillDataComponent_scenarioRef$key } from './__generated__/webSearchPill_AtlassianConversationAssistantAgent_WebSearchPillDataComponent_scenarioRef.graphql.ts';
import type { webSearchPill_AtlassianConversationAssistantAgent_WebSearchPillListDataComponent_scenarioListRef$key } from './__generated__/webSearchPill_AtlassianConversationAssistantAgent_WebSearchPillListDataComponent_scenarioListRef.graphql.ts';
import { messages } from './messages.ts';

const styles = cssMap({
	wrapper: {
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.small'),
		paddingRight: token('space.050'),
		paddingLeft: token('space.050'),
		paddingBlock: token('space.025'),
		width: 'fit-content',
	},
});

/**
 * UI component for the web search pill
 */
export const WebSearchPill = (): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Inline space="space.050" alignBlock="center" xcss={styles.wrapper}>
			<GlobeIcon label="" color={token('color.icon')} />
			<Text>{formatMessage(messages.webSearchPill)}</Text>
			<Lozenge appearance="discovery">{formatMessage(messages.betaTag)}</Lozenge>
		</Inline>
	);
};

/**
 * Get the web search enabled status from a SINGLE scenario and render the pill if it is enabled
 */
export const WebSearchPillDataComponent = ({
	scenarioRef,
}: {
	scenarioRef: webSearchPill_AtlassianConversationAssistantAgent_WebSearchPillDataComponent_scenarioRef$key;
}): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment webSearchPill_AtlassianConversationAssistantAgent_WebSearchPillDataComponent_scenarioRef on AgentStudioBaseConfiguration {
				isWebSearchEnabled
				isWebSearchEnabledForOrganization
			}
		`,
		scenarioRef,
	);

	if (!data?.isWebSearchEnabled && !data?.isWebSearchEnabledForOrganization) {
		return <></>;
	}

	return <WebSearchPill />;
};

/**
 * Get LIST of scenarios and check if any of them has web search enabled
 */
export const WebSearchPillListDataComponent = ({
	scenarioListRef,
}: {
	scenarioListRef: webSearchPill_AtlassianConversationAssistantAgent_WebSearchPillListDataComponent_scenarioListRef$key;
}): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment webSearchPill_AtlassianConversationAssistantAgent_WebSearchPillListDataComponent_scenarioListRef on AgentStudioScenariosResult {
				edges {
					node {
						isWebSearchEnabledForScenarioAndOrganization
					}
				}
			}
		`,
		scenarioListRef,
	);

	const hasWebSearchEnabled = data?.edges?.some(
		(edge) => edge?.node?.isWebSearchEnabledForScenarioAndOrganization,
	);

	if (!hasWebSearchEnabled) {
		return <></>;
	}

	return <WebSearchPill />;
};
